import { useState } from "react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { validateStgQaGrant, STG_QA_MIN_NC, STG_QA_MAX_NC } from "@/lib/stgQaGrant";

// STG 테스트 크레딧 지급 버튼 + 모달 (B-2).
// STG 뷰(env==="stg")에서만 렌더된다 — 호출부(UserInlineDetail)가 조건부로 노출한다.
// PROD 크레딧 지급 UI/로직과 완전히 분리된 별개 컴포넌트이며, 전용 QA API(/stg-qa/grant)만 호출한다.
//
// props:
//   userId (sub)  — 현재 상세 사용자. 지급 대상.
//   email         — 표시용(선택).
//   currentBalance— 표시용(선택).
//   onGranted()   — 성공 시 호출부가 잔액/충전내역 즉시 재조회하도록 알림.
const MIN_NC = STG_QA_MIN_NC;
const MAX_NC = STG_QA_MAX_NC;

export default function StgQaCreditButton({ userId, email, currentBalance, onGranted }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // {amount, transactionId}

  function reset() {
    setAmount(""); setReason(""); setError(null); setDone(null); setBusy(false);
  }

  function close() {
    if (busy) return;
    setOpen(false);
    reset();
  }

  const { amtNum, amountValid: amtValid, reasonValid } = validateStgQaGrant({ amount, reason });
  const canSubmit = amtValid && reasonValid && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await analyticsApi.stgQaGrant({
        sub: userId,
        email: email || undefined,
        amount: amtNum,
        reason: reason.trim(),
      });
      setDone({ amount: res.amount, transactionId: res.transactionId });
      if (typeof onGranted === "function") onGranted({ amount: res.amount, transactionId: res.transactionId });
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(String(e), 0, "UNKNOWN"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
      >
        🟠 STG 테스트 크레딧 지급
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={close}>
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
              🟠 STAGING · 테스트 크레딧
            </div>
            <h3 className="text-base font-semibold text-ink-900">STG 테스트 크레딧 지급</h3>
            <p className="mt-1 text-xs text-ink-500">
              이 지급은 <b>STG 테스트 크레딧</b>이며 운영(PROD) 크레딧과 무관합니다.
              staging.ainova.news 테스트에만 사용됩니다.
            </p>

            <dl className="mt-3 rounded-lg bg-ink-50 p-3 text-xs">
              <div className="flex justify-between"><dt className="text-ink-500">대상</dt>
                <dd className="font-medium text-ink-800">{email || userId}</dd></div>
              {currentBalance != null && (
                <div className="mt-1 flex justify-between"><dt className="text-ink-500">현재 STG 잔액</dt>
                  <dd className="font-medium tabular-nums text-ink-800">{Number(currentBalance).toLocaleString()} NC</dd></div>
              )}
            </dl>

            {done ? (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                +{done.amount.toLocaleString()} NC 지급 완료. (tx {done.transactionId?.slice(0, 8)}…)
              </div>
            ) : (
              <>
                <label className="mt-4 block text-xs font-medium text-ink-600">
                  지급 금액 (NC) — {MIN_NC}~{MAX_NC.toLocaleString()}
                  <input
                    type="number" min={MIN_NC} max={MAX_NC} value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="mt-1 w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm tabular-nums"
                    placeholder="예: 50000"
                  />
                  {amount !== "" && !amtValid && (
                    <span className="mt-0.5 block text-[11px] text-red-600">
                      {MIN_NC}~{MAX_NC.toLocaleString()} 사이 정수여야 합니다.
                    </span>
                  )}
                </label>

                <label className="mt-3 block text-xs font-medium text-ink-600">
                  사유 (3자 이상)
                  <input
                    type="text" value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 w-full rounded-md border border-ink-200 px-2.5 py-1.5 text-sm"
                    placeholder="예: QA 재충전"
                  />
                  {reason !== "" && !reasonValid && (
                    <span className="mt-0.5 block text-[11px] text-red-600">사유는 3자 이상 입력해 주세요.</span>
                  )}
                </label>

                {error && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    지급 실패 — {error.message}
                  </div>
                )}
              </>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={close} disabled={busy}
                className="rounded-md border border-ink-200 px-3 py-1.5 text-sm text-ink-600 hover:bg-ink-100 disabled:opacity-50">
                {done ? "닫기" : "취소"}
              </button>
              {!done && (
                <button type="button" onClick={submit} disabled={!canSubmit}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
                  {busy ? "지급 중…" : "지급"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
